/**
 * Teacher Dashboard — "Clean Academy" light / "Command Center" dark
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Settings, Users, BarChart3, BookOpen, AlertCircle, Loader2,
  Trash2, ArrowRight, FlaskConical, Target, Zap, TrendingUp, GraduationCap,
} from 'lucide-react';
import { UserProfileButton, useCurrentUser } from './AuthComponents';
import ThemeToggle from './guided/titration/panels/ThemeToggle';
import { useTeacherExperiments, useExperimentStats, useDeleteExperiment } from '../utils/convexHooks';
import { ClassCodeBadge } from './ClassCodeDisplay';
import { Id } from '../convex/_generated/dataModel';

// ── Shared header used by both tabs ──────────────────────────────────────
function DashboardHeader() {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-50 bg-white/80 dark:bg-[#030712]/80 backdrop-blur-xl border-b border-blue-100/80 dark:border-violet-500/10 transition-all">
      <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-3.5">
        <div className="flex items-center justify-between gap-4">
          {/* Brand */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 group focus:outline-none"
          >
            <img src="/logo_only.png" alt="HoloLab" className="w-10 h-10 sm:w-11 sm:h-11 object-contain transition-transform duration-300 group-hover:scale-105" />
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight font-space leading-none">HoloLab</h1>
              <p className="text-[11px] font-semibold text-blue-500 dark:text-violet-400 uppercase tracking-widest leading-none mt-0.5">Instructor Dashboard</p>
            </div>
          </button>

          {/* Controls */}
          <div className="flex items-center gap-2 sm:gap-3 bg-slate-100/80 dark:bg-white/5 p-1.5 rounded-full border border-slate-200/50 dark:border-violet-500/10 backdrop-blur-md">
            <ThemeToggle />
            <UserProfileButton />
          </div>
        </div>
      </div>
    </header>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────
export default function TeacherDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'experiments' | 'analytics'>('experiments');

  return (
    <div className="min-h-screen bg-[#f0f4ff] dark:bg-[#030712] transition-colors duration-500 selection:bg-blue-200 dark:selection:bg-violet-900">
      {/* Subtle light-mode gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/60 via-transparent to-teal-50/40 dark:from-transparent dark:via-transparent dark:to-transparent" />
        {/* Dark-mode aurora */}
        <div className="hidden dark:block absolute -top-1/3 -right-1/4 w-[50vw] h-[50vw] rounded-full animate-aurora pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 65%)' }} />
        <div className="hidden dark:block absolute -bottom-1/4 -left-1/4 w-[40vw] h-[40vw] rounded-full animate-aurora2 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.05) 0%, transparent 65%)' }} />
      </div>

      <DashboardHeader />

      <main className="relative z-10 animate-in slide-in-from-bottom-8 duration-700 ease-out w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-8 lg:py-10">

        {/* Page hero row */}
        <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <GraduationCap className="w-5 h-5 text-blue-500 dark:text-violet-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-blue-500 dark:text-violet-400 font-space">Teacher Portal</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight font-space">
              My Experiments
            </h2>
            <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
              Deploy virtual labs, monitor live student progress, and grade AI-assisted discussions.
            </p>
          </div>

          <button
            onClick={() => navigate('/teacher/experiment/create')}
            className="group relative flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm font-space text-white shrink-0 transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 bg-blue-600 dark:bg-gradient-to-r dark:from-violet-600 dark:to-cyan-500 hover:bg-blue-700 dark:hover:from-violet-500 dark:hover:to-cyan-400 shadow-lg shadow-blue-500/25 dark:shadow-violet-500/25 hover:shadow-xl hover:shadow-blue-500/30 dark:hover:shadow-violet-500/30"
          >
            <Plus className="w-4 h-4" />
            Launch New Lab
            <Zap className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-7">
          <div className="inline-flex gap-1 p-1 bg-slate-200/60 dark:bg-white/5 backdrop-blur-md rounded-xl border border-slate-200/50 dark:border-violet-500/10">
            {([
              { key: 'experiments', label: 'Active Labs',     Icon: BookOpen },
              { key: 'analytics',   label: 'Class Insights',  Icon: Target  },
            ] as const).map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm font-space transition-all duration-200 ${
                  activeTab === key
                    ? 'bg-white dark:bg-violet-600/20 text-blue-600 dark:text-violet-300 shadow-sm dark:shadow-violet-500/10 dark:border dark:border-violet-500/20'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          {activeTab === 'experiments' && <ExperimentsTab />}
          {activeTab === 'analytics'   && <AnalyticsTab />}
        </div>
      </main>
    </div>
  );
}

// ── Experiments Tab ───────────────────────────────────────────────────────
function ExperimentsTab() {
  const { user } = useCurrentUser();
  const experiments = useTeacherExperiments(user?.id);
  const deleteExperiment = useDeleteExperiment();
  const navigate = useNavigate();

  const handleDelete = async (id: Id<"experimentConfigs">) => {
    if (window.confirm("Delete this experiment? All student data will be lost. This cannot be undone.")) {
      await deleteExperiment({ experimentId: id });
    }
  };

  if (experiments === undefined) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="relative">
          <div className="absolute inset-0 bg-blue-500/20 dark:bg-violet-500/20 blur-xl rounded-full" />
          <Loader2 className="w-10 h-10 text-blue-500 dark:text-violet-400 animate-spin relative z-10" />
        </div>
        <p className="text-slate-400 font-medium tracking-widest uppercase text-xs animate-pulse font-space">Syncing Lab Data…</p>
      </div>
    );
  }

  if (!user || experiments === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-2xl p-8 text-center max-w-sm w-full">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-red-700 dark:text-red-400 mb-1 font-space">Connection Error</h3>
          <p className="text-sm text-red-600/80 dark:text-red-400/70">Unable to load lab data. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  if (experiments.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-blue-50 dark:bg-violet-900/20 border border-blue-100 dark:border-violet-500/20">
            <FlaskConical className="w-9 h-9 text-blue-500 dark:text-violet-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 font-space">The Lab is Empty</h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm leading-relaxed">
            Create your first interactive experiment and share the class code with students.
          </p>
          <button
            onClick={() => navigate('/teacher/experiment/create')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-gradient-to-r dark:from-violet-600 dark:to-cyan-500 text-white rounded-xl font-semibold text-sm font-space shadow-lg shadow-blue-500/25 dark:shadow-violet-500/25 hover:-translate-y-0.5 transition-all duration-300"
          >
            Configure First Lab
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6">
      {experiments.map((exp) => (
        <ExperimentCard
          key={exp._id}
          exp={exp}
          onMonitor={() => navigate(`/teacher/experiment/${exp._id}/students`)}
          onSettings={() => navigate(`/teacher/experiment/${exp._id}/settings`)}
          onDelete={() => handleDelete(exp._id)}
        />
      ))}
    </div>
  );
}

// ── Experiment Card ───────────────────────────────────────────────────────
function ExperimentCard({
  exp,
  onMonitor,
  onSettings,
  onDelete,
}: {
  exp: any;
  onMonitor: () => void;
  onSettings: () => void;
  onDelete: () => void;
}) {
  const isTest = exp.experimentMode === 'test';

  return (
    <div className="group relative flex flex-col bg-white dark:bg-[#0d0d1a] rounded-2xl overflow-hidden border border-slate-200/80 dark:border-violet-500/10 shadow-sm hover:shadow-xl hover:shadow-blue-500/8 dark:hover:shadow-violet-500/10 hover:-translate-y-1 transition-all duration-300">
      {/* Top accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-blue-500 to-teal-400 dark:from-violet-500 dark:to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Blue left border accent on hover — light mode only */}
      <div className="absolute left-0 top-1 bottom-0 w-0.5 bg-blue-500 dark:hidden opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="p-5 sm:p-6 flex flex-col flex-1 gap-4">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mb-2 leading-tight truncate font-space">
              {exp.title}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <ClassCodeBadge classCode={exp.classCode} />
              <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide border ${
                isTest
                  ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800/40 dark:text-red-400'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-900/20 dark:border-emerald-800/40 dark:text-emerald-400'
              }`}>
                {exp.experimentMode}
              </span>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="bg-slate-50/80 dark:bg-white/[0.03] rounded-xl p-3.5 border border-slate-100 dark:border-violet-500/8 space-y-2.5">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 font-medium text-slate-500 dark:text-slate-400">
              <Users className="w-3.5 h-3.5" /> Enrolled
            </span>
            <EnrollmentCount experimentId={exp._id} />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-slate-500 dark:text-slate-400">Deployed</span>
            <span className="font-semibold text-slate-700 dark:text-slate-300 text-xs">
              {new Date(exp._creationTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-auto flex gap-2">
          <button
            onClick={onMonitor}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold font-space transition-all duration-200 bg-blue-50 dark:bg-violet-500/10 text-blue-600 dark:text-violet-300 border border-blue-100 dark:border-violet-500/15 hover:bg-blue-600 dark:hover:bg-violet-600 hover:text-white dark:hover:text-white hover:border-blue-600 dark:hover:border-violet-600"
          >
            Monitor Class
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </button>
          <button
            onClick={onSettings}
            className="p-2.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/50 dark:border-white/5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-700 dark:hover:text-white transition-all duration-200"
            aria-label="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2.5 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/25 transition-all duration-200"
            aria-label="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Enrollment Count ──────────────────────────────────────────────────────
function EnrollmentCount({ experimentId }: { experimentId: string }) {
  const stats = useExperimentStats(experimentId as Id<"experimentConfigs">);
  if (stats === undefined) return <Loader2 className="w-4 h-4 animate-spin text-blue-400 dark:text-violet-400" />;
  return (
    <span className="font-bold text-blue-600 dark:text-violet-300 px-2 py-0.5 bg-blue-50 dark:bg-violet-900/25 rounded-md text-xs">
      {stats?.totalStudents || 0} students
    </span>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────
function AnalyticsTab() {
  const { user } = useCurrentUser();
  const experiments = useTeacherExperiments(user?._id);

  const stats = [
    {
      label: 'Active Labs',
      value: experiments?.length ?? '—',
      icon: BookOpen,
      color: 'text-blue-600 dark:text-violet-400',
      bg: 'bg-blue-50 dark:bg-violet-900/20',
      border: 'border-blue-100 dark:border-violet-500/15',
    },
    {
      label: 'Total Students',
      value: '—',
      sub: 'Across all labs',
      icon: Users,
      color: 'text-teal-600 dark:text-cyan-400',
      bg: 'bg-teal-50 dark:bg-cyan-900/20',
      border: 'border-teal-100 dark:border-cyan-500/15',
    },
    {
      label: 'Avg. Completion',
      value: '87%',
      sub: '+5% from last month',
      icon: TrendingUp,
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      border: 'border-emerald-100 dark:border-emerald-500/15',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {stats.map(({ label, value, sub, icon: Icon, color, bg, border }) => (
          <div key={label} className={`bg-white dark:bg-[#0d0d1a] rounded-2xl p-6 border ${border} shadow-sm`}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 font-space">{label}</span>
              <div className={`w-10 h-10 ${bg} ${border} border rounded-xl flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${color}`} />
              </div>
            </div>
            <p className={`text-4xl font-bold ${color} font-space tracking-tight`}>{value}</p>
            {sub && <p className="text-xs font-medium text-emerald-500 mt-2">{sub}</p>}
          </div>
        ))}
      </div>

      {/* Coming soon */}
      <div className="bg-white dark:bg-[#0d0d1a] rounded-2xl p-8 border border-slate-200/80 dark:border-violet-500/10 text-center">
        <BarChart3 className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1 font-space">Detailed Analytics Coming Soon</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">Student completion rates, discussion scores, and lab performance charts will appear here.</p>
      </div>
    </div>
  );
}
